import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Trophy, Calendar, Clock, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AllMatches() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/all`, { withCredentials: true });
      setMatches(response.data);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error('Please login to view matches');
        navigate('/login');
      } else {
        toast.error('Failed to load matches');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#007AFF] mx-auto mb-4"></div>
          <p className="text-gray-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToDashboard')}
          </Button>
          <Logo size="lg" />
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3">
            <Trophy className="w-10 h-10 text-[#007AFF]" />
            <div>
              <h1 className="text-3xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t('allMatches')}
              </h1>
              <p className="text-gray-400">{t('allMatchesDesc')}</p>
            </div>
          </div>
        </div>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              {matches.length} {t('matches')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {matches.length === 0 ? (
              <p className="text-gray-400 text-center py-8">{t('noMatchesYet')}</p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {matches.map((match) => (
                  <div 
                    key={match.match_id}
                    className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 hover:border-[#007AFF]/50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      {/* Players and Score */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs bg-[#007AFF]/20 text-[#007AFF] px-2 py-1 rounded-full">
                            {match.ranking_list_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className={`flex-1 text-right ${match.winner_id === match.player1_id ? 'text-green-400 font-bold' : 'text-white'}`}>
                            <p>{match.player1_nickname || match.player1_name}</p>
                          </div>
                          <div className="flex items-center gap-2 bg-[#27272A] px-4 py-2 rounded-lg">
                            <span className={`text-xl font-bold ${match.winner_id === match.player1_id ? 'text-green-400' : 'text-white'}`}>
                              {match.player1_score}
                            </span>
                            <span className="text-gray-500">:</span>
                            <span className={`text-xl font-bold ${match.winner_id === match.player2_id ? 'text-green-400' : 'text-white'}`}>
                              {match.player2_score}
                            </span>
                          </div>
                          <div className={`flex-1 ${match.winner_id === match.player2_id ? 'text-green-400 font-bold' : 'text-white'}`}>
                            <p>{match.player2_nickname || match.player2_name}</p>
                          </div>
                        </div>
                        {/* ELO Changes */}
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <div className="flex-1 text-right">
                            <span className={match.elo_change_p1 > 0 ? 'text-green-400' : 'text-red-400'}>
                              {match.elo_change_p1 > 0 ? '+' : ''}{match.elo_change_p1}
                            </span>
                          </div>
                          <div className="w-20 text-center text-gray-500 text-xs">ELO</div>
                          <div className="flex-1">
                            <span className={match.elo_change_p2 > 0 ? 'text-green-400' : 'text-red-400'}>
                              {match.elo_change_p2 > 0 ? '+' : ''}{match.elo_change_p2}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Timestamps */}
                      <div className="text-xs text-gray-400 sm:text-right space-y-1 sm:min-w-[180px]">
                        <div className="flex items-center gap-1 sm:justify-end">
                          <Calendar className="w-3 h-3" />
                          <span>{t('challengedAt')}: {formatDate(match.challenged_at || match.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1 sm:justify-end">
                          <Clock className="w-3 h-3" />
                          <span>{t('confirmedAt')}: {formatDate(match.confirmed_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AllMatches;
